import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './Constants';
// const API_BASE_URL = 'http://localhost:8000'; // Replace with your Laravel API base URL

const ListAllUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, selectedRole]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/products`);
      setUsers(res.data);
      setError('');
    } catch (err) {
      console.error('Error fetching users', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    if (selectedRole === 'all') {
      setFilteredUsers(users);
    } else {
      setFilteredUsers(users.filter(user => user.role === selectedRole));
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/products/${userId}`);
      setUsers(users.filter(user => user.id !== userId));
      setError('');
    } catch (err) {
      console.error('Error deleting user', err);
      setError('Failed to delete user');
    }
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold text-center mb-8 bg-secondary text-white">Dashboard</h1>
      
      {error && (
        <div className="alert alert-error mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Filter Controls */}
      <div className="flex justify-center mb-6">
        <div className="join">
          <button
            className={`join-item btn ${selectedRole === 'all' ? 'btn-primary' : ''}`}
            onClick={() => handleRoleChange('all')}
          >
            All Users
          </button>
          <button
            className={`join-item btn ${selectedRole === 'student' ? 'btn-primary' : ''}`}
            onClick={() => handleRoleChange('student')}
          >
            Students
          </button>
          <button
            className={`join-item btn ${selectedRole === 'tutor' ? 'btn-primary' : ''}`}
            onClick={() => handleRoleChange('tutor')}
          >
            Tutors
          </button>
          <button
            className={`join-item btn ${selectedRole === 'admin' ? 'btn-primary' : ''}`}
            onClick={() => handleRoleChange('admin')}
          >
            Admins
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Password</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Syllabus</th>
              <th>Grade/Experience</th>
              <th>Subjects</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="avatar placeholder">
                      <div className="bg-primary text-neutral-content flex items-center justify-center rounded-full w-8">
                        <span className="text-xl flex justify-center items-center">{user.name.charAt(0).toUpperCase()}</span>
                      </div>
                    </div>
                    <div>
                      <div className="font-bold">{user.name}</div>
                    </div>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>{user.password}</td>
                <td>{user.phone || 'N/A'}</td>
                <td>
                  <span className={`badge ${
                    user.role === 'admin' ? 'badge-error' :
                    user.role === 'tutor' ? 'badge-warning' :
                    'badge-success'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td>{user.syllabus || 'N/A'}</td>
                <td>
                  {user.role === 'student' ? user.grade || 'N/A' : user.experience || 'N/A'}
                </td>
                <td>
                  {user.role === 'student' ? user.student_subject || 'N/A' : user.tutor_subject || 'N/A'}
                </td>
                <td>
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="btn btn-error btn-sm"
                    disabled={user.role === 'admin'} // Prevent deleting admins
                    title={user.role === 'admin' ? 'Cannot delete admin users' : 'Delete user'}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No users found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListAllUsers;